import {
  APPLICANT_AUTOMATION_COMMUNICATION_TYPES,
  APPLICANT_AUTOMATION_DISPLAY_CONDITION_TYPES,
  CREATABLE_APPLICANT_AUTOMATION_TYPES,
  CreatableApplicantAutomationTypes,
} from './applicant-automation-communication-types';
import { isEmployeeAutomationTrigger } from './employee-automation-display-utils';

import {
  ACCEPTED_STATUS,
  APPLIED_STATUS,
  statusDisplay,
} from '../../config/application-status-config';
import { AUTOMATED_LABEL_KEYS } from '../../util/automated-label-keys';
import { capitalize, humanizeSnakeCaseString } from '../../util/formatters';

type ApplicantAutomationDisplayDictionary = {
  [trigger: string]: {
    title: string;
    automations: ApplicantAutomation[];
  };
};

export type AutomationsByTriggerDictionary = { [trigger: string]: ApplicantAutomation[] };

export const ConfirmableMessageText = 'Please reply YES to confirm, NO to cancel';

export const ConfirmRevertToDefaultText =
  'Are you sure you want to reset this custom message to the organization default? This action cannot be undone.';

const capitalizeCommunicationType = (communicationType: string) =>
  humanizeSnakeCaseString(communicationType);

export const getEmailSubject = (jobTitle: string, orgName?: string, forInterview = false) => {
  if (forInterview) {
    return `Your Interview for ${jobTitle}${orgName ? ` with ${orgName}` : ''}`;
  }
  return `Your Application for ${jobTitle}${orgName ? ` at ${orgName}` : ''}`;
};

export const getTitleWithFallback = (automation: ApplicantAutomation) => {
  if (automation.description) {
    return automation.description;
  }

  return getDefaultTitleFromStatusAndCommunicationType(
    automation.communicationType,
    automation.toStatus,
    automation.isFollowUp,
  );
};

export const isApplicantAutomationTrigger = (
  trigger:
    | 'applicant'
    | CreatableApplicantAutomationTypes
    | StartDateTriggeredCommunicationTriggerType,
): trigger is CreatableApplicantAutomationTypes =>
  !isEmployeeAutomationTrigger(trigger) && trigger !== 'applicant';

export const getApplicantAutomationCreateDefaults = (
  trigger:
    | 'applicant'
    | CreatableApplicantAutomationTypes
    | StartDateTriggeredCommunicationTriggerType,
): Partial<ApplicantAutomationCopy> => {
  if (!isApplicantAutomationTrigger(trigger)) {
    return {};
  }

  if (trigger.includes('STATUS_CHANGE')) {
    const communicationType = APPLICANT_AUTOMATION_COMMUNICATION_TYPES.STATUS_CHANGE;
    const toStatus = trigger.replace('STATUS_CHANGE_', '') as ApplicationStatus;

    return {
      communicationType,
      toStatus,
      description: getDefaultTitleFromStatusAndCommunicationType(communicationType, toStatus),
    } as Partial<ApplicantAutomationCopy>;
  }

  return {
    communicationType: trigger,
    description: getDefaultTitleFromStatusAndCommunicationType(
      trigger as ApplicantAutomationCommunicationType,
    ),
  } as Partial<ApplicantAutomationCopy>;
};

export const getDefaultTitleFromStatusAndCommunicationType = (
  communicationType: ApplicantAutomationCommunicationType,
  toStatus?: ApplicationStatus | null,
  isFollowUp?: boolean | null,
) => {
  let title = '';
  if (toStatus === APPLIED_STATUS) {
    title = 'Application Receipt Confirmation';
  } else if (toStatus === ACCEPTED_STATUS) {
    title = 'Schedule Interview Request';
  } else if (toStatus) {
    title = `${statusDisplay(toStatus)} Notification`;
  } else if (communicationType === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.FIRST_DAY_REMINDER) {
    title = 'Start Date Reminder';
  } else if (communicationType === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.WORK_HISTORY) {
    title = 'Missing Information Message';
  } else {
    title = capitalizeCommunicationType(communicationType);
  }

  return `${title}${isFollowUp ? ' Follow Up' : ''}`;
};

export const getApplicationAutomationTiming = (applicantAutomation: ApplicantAutomation) => {
  switch (applicantAutomation.communicationType) {
    case APPLICANT_AUTOMATION_COMMUNICATION_TYPES.STATUS_CHANGE:
      if (applicantAutomation.toStatus === APPLIED_STATUS) {
        return `Sent ${
          applicantAutomation.sendTimeOffsetHours
            ? `${applicantAutomation.sendTimeOffsetHours} hour(s)`
            : 'immediately'
        } after the application is received`;
      }
      return applicantAutomation.sendTimeOffsetHours
        ? `Follow up ${
            applicantAutomation.sendTimeOffsetHours
          } hour(s) after you click ${statusDisplay(applicantAutomation.toStatus)}${
            applicantAutomation.displayCondition ===
            APPLICANT_AUTOMATION_DISPLAY_CONDITION_TYPES.INOVA_SUBSCRIPTION
              ? ' because you are connected to Inova'
              : ''
          }`
        : `Sent immediately after you click ${statusDisplay(applicantAutomation.toStatus)}${
            applicantAutomation.displayCondition ===
            APPLICANT_AUTOMATION_DISPLAY_CONDITION_TYPES.INOVA_SUBSCRIPTION
              ? ' because you are connected to Inova'
              : ''
          }`;
    case APPLICANT_AUTOMATION_COMMUNICATION_TYPES.WORK_HISTORY:
      return `Sent ${
        applicantAutomation.sendTimeOffsetHours === 0
          ? 'Immediately'
          : `${applicantAutomation.sendTimeOffsetHours} hour(s)`
      } after the application is received if the application is incomplete`;
    case APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION:
      return `Sent ${
        applicantAutomation.sendTimeOffsetHours === 0
          ? 'Immediately'
          : `${applicantAutomation.sendTimeOffsetHours} hour(s)`
      } after application is completed`;
    case APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTERVIEW_REMINDER:
      return `Sent ${
        applicantAutomation.sendTimeOffsetHours === 0
          ? 'Immediately'
          : `${Math.abs(applicantAutomation.sendTimeOffsetHours)} hour(s)`
      } ${applicantAutomation.sendTimeOffsetHours < 0 ? 'before' : 'after'} interview`;
    case APPLICANT_AUTOMATION_COMMUNICATION_TYPES.FIRST_DAY_REMINDER:
      return `Sent ${
        applicantAutomation.sendTimeOffsetHours === 0
          ? 'Immediately'
          : `${Math.abs(applicantAutomation.sendTimeOffsetHours)} hour(s)`
      } ${
        applicantAutomation.sendTimeOffsetHours < 0 ? 'before' : 'after'
      } new employee's first day${
        applicantAutomation.displayCondition ===
        APPLICANT_AUTOMATION_DISPLAY_CONDITION_TYPES.INOVA_SUBSCRIPTION
          ? ' because you are connected to Inova'
          : ''
      }`;
    default:
      // we shouldn't ever be here
      return '';
  }
};

export const getFollowUpsToUpdate = (isActive: boolean, followUps: Array<ApplicantAutomation>) => {
  if (isActive) {
    return [];
  }

  return followUps.filter((followUpAutomation) => followUpAutomation.isActive);
};

export const getFollowUpDetails = (automation: ApplicantAutomation) =>
  `This message does not send if the applicant responds to the initial ${getDefaultTitleFromStatusAndCommunicationType(
    automation.communicationType,
    automation.toStatus,
  ).toLowerCase()}(s)`;

export const getSectionForApplicantAutomation = (automation: ApplicantAutomation) => {
  const communicationType =
    automation.communicationType === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.STATUS_CHANGE &&
    automation.toStatus
      ? `${automation.communicationType}_${automation.toStatus}`
      : automation.communicationType;
  switch (communicationType) {
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_APPLIED:
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.INTEREST_CONFIRMATION:
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.WORK_HISTORY: {
      return 'application';
    }
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_ACCEPTED:
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_PHONE_INTERVIEWING:
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_INTERVIEWING:
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.INTERVIEW_REMINDER:
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_SAVED:
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_REJECTED: {
      return 'hiring';
    }
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_VALIDATING:
    case CREATABLE_APPLICANT_AUTOMATION_TYPES.FIRST_DAY_REMINDER: {
      return 'onboarding';
    }
    default:
      throw new Error(
        `No matching section for automation ${automation.applicantTriggeredCommunicationId}`,
      );
  }
};

export const getOrderedApplicantAutomationTriggersBySection = (
  section: ApplicantAutomationSection,
): CreatableApplicantAutomationTypes[] => {
  switch (section) {
    case 'allApplicantAutomations':
      return getOrderedApplicantAutomationTriggersBySection('application').concat(
        getOrderedApplicantAutomationTriggersBySection('hiring').concat(
          getOrderedApplicantAutomationTriggersBySection('onboarding'),
        ),
      );
    case 'application':
      return [
        CREATABLE_APPLICANT_AUTOMATION_TYPES.WORK_HISTORY,
        CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_APPLIED,
        CREATABLE_APPLICANT_AUTOMATION_TYPES.INTEREST_CONFIRMATION,
      ] as Array<CreatableApplicantAutomationTypes>;
    case 'hiring':
      return [
        CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_ACCEPTED,
        CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_PHONE_INTERVIEWING,
        CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_INTERVIEWING,
        CREATABLE_APPLICANT_AUTOMATION_TYPES.INTERVIEW_REMINDER,
        CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_SAVED,
        CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_REJECTED,
      ] as Array<CreatableApplicantAutomationTypes>;
    case 'onboarding':
      return [
        CREATABLE_APPLICANT_AUTOMATION_TYPES.STATUS_CHANGE_VALIDATING,
        CREATABLE_APPLICANT_AUTOMATION_TYPES.FIRST_DAY_REMINDER,
      ] as Array<CreatableApplicantAutomationTypes>;
    default:
      throw new Error(`Unrecognized applicant automation section ${section}`);
  }
};

const getTriggerLabel = (trigger: string) => {
  let label = capitalizeCommunicationType(trigger);
  if (trigger.includes('STATUS_CHANGE')) {
    const status = trigger.replace('STATUS_CHANGE_', '');
    label = `Applicant Moved to ${statusDisplay(status)}`;
  } else if (trigger.includes('REMINDER')) {
    label = trigger.includes('INTERVIEW')
      ? 'Applicant Interview Starts'
      : 'Applicant Starts First Day';
  } else if (trigger === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.WORK_HISTORY) {
    label = 'Application Received with Missing Information';
  } else if (trigger === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION) {
    label = 'Application is Completed';
  }

  return label;
};

export const getApplicantAutomationTriggerOptionsBySection = (
  section: ApplicantAutomationSection,
): SelectOption<string>[] => {
  const triggersForSection = getOrderedApplicantAutomationTriggersBySection(section);

  return triggersForSection.map((trigger) => ({
    label: getTriggerLabel(trigger),
    value: trigger,
  }));
};

export const getTimelineTitlesForTriggersBySection = (
  section: ApplicantAutomationSection,
): Record<string, string> =>
  getOrderedApplicantAutomationTriggersBySection(section).reduce(
    (acc, trigger) => ({
      ...acc,
      [trigger]: capitalize(getTriggerLabel(trigger).toLowerCase()),
    }),
    {},
  );

export const getAllOverridesForOrgDefault = (
  orgDefault: ApplicantAutomation,
  automations: ApplicantAutomation[],
) =>
  automations.filter(
    (a) => a.overrideForCommunicationId === orgDefault.applicantTriggeredCommunicationId,
  );

export const sortAutomations = (automations: ApplicantAutomation[]) =>
  automations.sort((a, b) => {
    if (a.sendTimeOffsetHours === b.sendTimeOffsetHours) {
      return a.description.localeCompare(b.description);
    }

    return a.sendTimeOffsetHours - b.sendTimeOffsetHours;
  });

export const getAutomationsForContext = (
  automations: Array<ApplicantAutomation>,
  orgId: number,
  editingForJobId?: number,
) => {
  const orgDefaultAutomations = automations.filter((a) => a.defaultForOrgId === orgId);
  if (!editingForJobId) {
    return orgDefaultAutomations;
  }

  const jobOverrides = automations.filter((a) => a.jobId === editingForJobId);
  const overriddenAutomationIds = jobOverrides.map((a) => a.overrideForCommunicationId);

  return jobOverrides.concat(
    orgDefaultAutomations.filter(
      (a) => !overriddenAutomationIds.includes(a.applicantTriggeredCommunicationId),
    ),
  );
};

export const groupAutomationsByTitleForSection = (
  section: ApplicantAutomationSection,
  automations: ApplicantAutomation[],
  orgId: number,
  editingForJob?: Job,
): ApplicantAutomationDisplayDictionary => {
  const titles = getTimelineTitlesForTriggersBySection(section);
  const triggers: string[] = getOrderedApplicantAutomationTriggersBySection(section);

  const automationsForContext = getAutomationsForContext(automations, orgId, editingForJob?.job_id);

  const groupedAutomationsForSection = triggers.reduce(
    (acc: ApplicantAutomationDisplayDictionary, trigger: string) => {
      const titleForTrigger = titles[trigger];
      if (!titleForTrigger) {
        throw new Error(`Unrecognized applicant automation trigger ${trigger}`);
      }

      const automationsForTrigger = automationsForContext.filter((automation) => {
        if (trigger.includes('STATUS_CHANGE')) {
          const toStatus = trigger.replace('STATUS_CHANGE_', '');
          return (
            automation.toStatus === toStatus &&
            automation.communicationType === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.STATUS_CHANGE
          );
        }
        return automation.communicationType === trigger;
      });
      if (!automationsForTrigger.length) {
        return acc;
      }

      const sortedAutomations = sortAutomations(automationsForTrigger);

      return { ...acc, [trigger]: { title: titleForTrigger, automations: sortedAutomations } };
    },
    {},
  );

  return groupedAutomationsForSection;
};

export const emptyOption = (placeholderText: string) => [{ label: placeholderText, value: '' }];

export const getParentAutomationsByTriggerForSection = (
  triggers: string[],
  automations: ApplicantAutomationDisplayDictionary,
) =>
  triggers.reduce((acc: AutomationsByTriggerDictionary, trigger: string) => {
    acc[trigger] = automations[trigger]?.automations || [];
    return acc;
  }, {});

export const getFollowUpsForTrigger = (automations: ApplicantAutomation[] = []) =>
  automations.filter((automation) => !!automation.isFollowUp);

export const automationNeededForHqcMetric = (
  automation: ApplicantAutomation,
  allApplicantAutomations: ApplicantAutomation[],
  orgId: number,
  jobs: Job[],
  action: 'turn off' | 'delete' | 'revert',
) => {
  // Interest confirmations are how we determine the responsiveness of an applicant,
  // No other communication types are needed for an HQC metric as of now
  if (
    automation.communicationType !== APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION
  ) {
    return false;
  }

  const jobsWithResponsivenessHqc = jobs.filter((j) =>
    j.HqcSettings?.find((setting) => setting.automatedLabelKey === AUTOMATED_LABEL_KEYS.Responsive),
  );

  if (!jobsWithResponsivenessHqc.length) {
    // If we're here, no jobs use the responsive metric
    return false;
  }

  const parentInterestConfirmations = allApplicantAutomations.reduce(
    (acc: ApplicantAutomation[], a) => {
      if (
        a.defaultForOrgId === orgId &&
        a.communicationType === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION
      ) {
        acc.push(a);
      }
      return acc;
    },
    [],
  );

  let automationNeededActive = false;
  for (let i = 0; i < jobsWithResponsivenessHqc.length; i += 1) {
    const job = jobsWithResponsivenessHqc[i];

    // Here we need to check if the particular automation is the sole active interest confirmation for any job
    const activeInterestConfirmations = getAutomationsForContext(
      allApplicantAutomations,
      orgId,
      job.job_id,
    )
      .filter(
        (a) =>
          a.communicationType === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION,
      )
      .filter((interestConfirmation) => interestConfirmation.isActive);

    const onlyOneInterestConfirmation = activeInterestConfirmations.length === 1;
    let parentAutomationIsActive = false;
    if (action === 'revert' && onlyOneInterestConfirmation) {
      // If we are reverting a job override and there is an active parent automation, we can allow the override to be deleted
      // We cannot allow the override to be turned off even if the parent is active, however, because we will pull the inactive override as the relevant interest confirmation
      parentAutomationIsActive = !!parentInterestConfirmations.find(
        (parent) =>
          parent.applicantTriggeredCommunicationId === automation.overrideForCommunicationId,
      )?.isActive;
    }
    const isOnlyActiveInterestConfirmation =
      action === 'revert' ? !parentAutomationIsActive : onlyOneInterestConfirmation;

    if (
      isOnlyActiveInterestConfirmation &&
      activeInterestConfirmations[0].applicantTriggeredCommunicationId ===
        automation.applicantTriggeredCommunicationId
    ) {
      automationNeededActive = true;
      // If we need this automation for any job, we don't have to check the rest
      break;
    }
  }

  return automationNeededActive;
};

export const checkValidResponsiveHqcStateForOrg = (
  allApplicantAutomations: ApplicantAutomation[],
  orgId: number,
  jobs: Job[],
) => {
  const jobsWithResponsivenessHqc = jobs.filter((j) =>
    j.HqcSettings?.find((setting) => setting.automatedLabelKey === AUTOMATED_LABEL_KEYS.Responsive),
  );

  if (!jobsWithResponsivenessHqc.length) {
    return { isValidState: true };
  }

  let allJobsHaveOneActiveInterestConfirmation = true;
  for (let i = 0; i < jobsWithResponsivenessHqc.length; i += 1) {
    const job = jobsWithResponsivenessHqc[i];

    const interestConfirmations = getAutomationsForContext(
      allApplicantAutomations,
      orgId,
      job.job_id,
    ).filter(
      (automation) =>
        automation.communicationType ===
        APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION,
    );

    const activeInterestConfirmations = interestConfirmations.filter(
      (interestConfirmation) => interestConfirmation.isActive,
    );

    if (!interestConfirmations.length || !activeInterestConfirmations.length) {
      allJobsHaveOneActiveInterestConfirmation = false;
      // no need to check every job if we encounter an issue
      break;
    }
  }

  return allJobsHaveOneActiveInterestConfirmation;
};
